import { useEffect, useRef, useState } from "react";
import { validateEmail } from "@/utils/emailValidation";
import { validateRepresentativeEmailService } from "@/services/assistedAffiliation/validateRepresentativeEmailService";
import {
  EMAIL_FORMAT_ERROR,
  EMAIL_REQUIRED_ERROR,
  EMAIL_VERIFICATION_ERROR,
} from "../emailValidationConstants";
import type { EmailValidationState, PendingEmailValidation } from "./emailValidationTypes";

export const useRepresentativeEmailValidation = (email: string) => {
  const value = email.trim();
  const [state, setState] = useState<EmailValidationState>({
    email: value, status: "idle", message: "",
  });
  const currentEmail = useRef(value);
  currentEmail.current = value;
  const mounted = useRef(true);
  const pending = useRef<PendingEmailValidation | null>(null);
  const validated = useRef<EmailValidationState | null>(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      pending.current?.controller.abort();
    };
  }, []);

  useEffect(() => {
    pending.current?.controller.abort();
    pending.current = null;
    validated.current = null;
    setState({ email: value, status: "idle", message: "" });
  }, [value]);

  const validate = async (): Promise<boolean | null> => {
    if (!mounted.current) return null;
    const emailToValidate = currentEmail.current;
    const formatError = !emailToValidate
      ? EMAIL_REQUIRED_ERROR
      : !validateEmail(emailToValidate) ? EMAIL_FORMAT_ERROR : "";

    if (formatError) {
      setState({ email: emailToValidate, status: "invalid", message: formatError });
      return false;
    }

    if (validated.current?.email === emailToValidate) {
      return validated.current.status === "valid";
    }
    if (pending.current?.email === emailToValidate) return pending.current.promise;

    const controller = new AbortController();
    const isCurrent = () => mounted.current && !controller.signal.aborted &&
      currentEmail.current === emailToValidate;
    setState({ email: emailToValidate, status: "validating", message: "Validando correo electrónico..." });

    const promise = (async () => {
      try {
        const result = await validateRepresentativeEmailService(emailToValidate, controller.signal);
        if (!isCurrent()) return null;
        const nextState: EmailValidationState = {
          email: emailToValidate,
          status: result.valid ? "valid" : "invalid",
          message: result.message,
        };
        validated.current = nextState;
        setState(nextState);
        return result.valid;
      } catch {
        if (!isCurrent()) return null;
        setState({ email: emailToValidate, status: "error", message: EMAIL_VERIFICATION_ERROR });
        return false;
      } finally {
        if (pending.current?.controller === controller) pending.current = null;
      }
    })();

    pending.current = { email: emailToValidate, controller, promise };
    return promise;
  };

  return {
    state: state.email === value ? state : { email: value, status: "idle" as const, message: "" },
    validate,
  };
};
